import * as _ from "lodash";
import { Subject } from 'rxjs';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { InvoiceModel } from '@app/core/models/invoices/invoice.model';
import { InvoiceService } from '@app/core/services/invoice.service';
import { MultiSelectModel } from '@app/shared/models/multi-select.model';
import { IDropdownSettings } from 'ng-multiselect-dropdown';
import { TableIndication, TableRowAction, TableActionIcon } from '@app/shared/models/table-action-type.model';
import { InvoiceViewType } from '@app/core/models/invoices/invoice-view-type.model';
import { RejectionFlags } from '@app/core/models/referentials/rejection-flag.model';
import { ReferentialsModel } from '@app/core/models/referentials/referentials.model';
import { TableColumnsConfigKey } from '@app/core/models/configuration/table-columns-config-key.model';
import { GroupModel } from '@app/core/models/groups/group.model';
import { DropdownHelper } from '@app/shared/models/dropdown-helper.model';
import { ReferentialItem } from '@app/core/models/shared/referential-item.model';
import { SessionStorageService } from '@app/core/services/session-storage.service';
import { InvoiceFilterService } from '../invoice-filter-service';
import { InvoiceFilterConfig } from '@app/core/models/invoices/invoice-filter-config.model';
import { AssetService } from '@app/core/services/asset.service';
import { RecipientEntitiesService } from '@app/core/services/recipient-entities.service';
import { InvoiceFilters } from '@app/core/models/invoices/invoice-filters.model';
import { InvoiceSearchCriteriaModel } from '@app/core/models/invoices/invoice-search-criteria.model';
import { AuthenticationInfoService } from '@app/core/guards/authentication-info.service';
import { TableNavigationModel } from "@app/shared/models/table-navigation.model";

@Component({
  selector: 'app-invoice-list-rejected',
  templateUrl: './invoice-list-rejected.component.html',
  styleUrls: ['./invoice-list-rejected.component.scss']
})
export class InvoiceListRejectedComponent extends InvoiceFilterService implements OnInit, OnDestroy {

  invoices: InvoiceModel[] = [];
  data: InvoiceModel[] = [];
  tableActions: TableRowAction[] = [];
  tableIndications: TableIndication[] = [];

  pageIndex: number = 0;

  unsubscribe$: Subject<boolean> = new Subject<boolean>();

  dropdownListReverseFactoring: ReferentialItem[] = [];
  selectedItemsReverseFactoring: ReferentialItem[] = [];
  dropdownListWorkingSituation: ReferentialItem[] = [];
  selectedItemsWorkingSituation: ReferentialItem[] = [];
  dropdownListPicked: ReferentialItem[] = [];
  selectedItemsPicked: ReferentialItem[] = [];
  dropdownListRejectionFlag: ReferentialItem[] = [];
  selectedItemsRejectionFlag: ReferentialItem[] = [];

  dropdownListGroups: ReferentialItem[] = [];
  selectedItemsGroups: ReferentialItem[] = [];

  dropdownSettings: IDropdownSettings = {};
  dropdownMultiSelectWithSearchSettings: IDropdownSettings = {}

  recipientEntitiesDropdownList: ReferentialItem[] = [];
  selectedRecipientEntitiesItems: ReferentialItem[] = [];
  assetsDropdownList: ReferentialItem[] = [];
  selectedAssetsItems: ReferentialItem[] = [];

  validRejectionFlags: RejectionFlags[] = [RejectionFlags.NonCompliant, RejectionFlags.Duplicate, RejectionFlags.NonQualified]
  configKey: string = TableColumnsConfigKey.INVOICEREJECTED;
  exportFileName: string;
  panelOpenState: boolean = true;

  title: string;

  constructor(
    public route: ActivatedRoute,
    private router: Router,
    public assetService: AssetService,
    public recipientEntityService: RecipientEntitiesService,
    private invoiceService: InvoiceService,
    private dropdownHelper: DropdownHelper,
    private multiSelectModel: MultiSelectModel,
    public sessionStorageService: SessionStorageService,
    private authenticationInfoService: AuthenticationInfoService,
  ) {
    super(route, sessionStorageService, assetService, recipientEntityService);
    this.title = "FACTURES EN REJET";
    this.exportFileName = 'Factures_En_Reject';
    this.invoiceFiltersConfig = new InvoiceFilterConfig(true, true, true, true, false, false, true, true, true, false, true, true, true);
  }

  ngOnInit() {
    this.route.data.subscribe(data => {
      const referentials: ReferentialsModel = data.referentials;
      this.dropdownListRejectionFlag = referentials.RejectionFlags.filter((rejectionFlag => this.validRejectionFlags.includes(rejectionFlag.Id)))
      this.dropdownListGroups = data.groups.filter((g: GroupModel) => g.IsActive).map((g: GroupModel) => new ReferentialItem(g.Id, g.Name));
    });

    let groupsFilter = null;
    this.restoreFilters(data => {
      groupsFilter = data.Groups;
      this.selectedAssetsItems = data?.Assets ?? [];
      this.selectedRecipientEntitiesItems = data?.RecipientEntities ?? [];
      this.selectedItemsPicked = data?.Picked ?? [];
      this.selectedItemsRejectionFlag = data?.RejectionTypes ?? [];
      this.selectedItemsReverseFactoring = data?.ReverseFactoring ?? [];
      this.selectedItemsWorkingSituation = data?.WorkingSituation ?? [];
    });
    this.restoreGroupsFilter(groupsFilter);

    const pageNavigation = Object.assign({}, JSON.parse(localStorage.getItem(`table_config_${this.configKey}`))?.pageDef) as TableNavigationModel; //Persiste filter
    this.pageIndex = pageNavigation?.Page?.pageIndex ?? 0;

    this.dropdownListReverseFactoring = this.dropdownHelper.getYesOrNoDropdownList();
    this.dropdownListWorkingSituation = this.dropdownHelper.getYesOrNoDropdownList();
    this.dropdownListPicked = this.dropdownHelper.getYesOrNoDropdownList();
    this.dropdownSettings = this.multiSelectModel.getSettings();
    this.dropdownMultiSelectWithSearchSettings = this.multiSelectModel.getSettings('Id', 'Label', false, true);
    this.tableActions.push(new TableRowAction(TableActionIcon.ZOOM, 'Voir le détail de la facture', this.onZoom));
    this.tableActions.push(new TableRowAction(TableActionIcon.PDF, 'Ouvrir la facture', this.onOpenPdf));
    this.tableIndications.push(new TableIndication(TableActionIcon.ERROR, "L'association Référence 1 et société n'est pas conforme", (invoice: InvoiceModel) => invoice.SupplierHasWarning));
  }

  restoreGroupsFilter(groups: ReferentialItem[]): void {
    if (_.isNil(groups)) {
      this.selectedItemsGroups = this.dropdownListGroups.filter((g: ReferentialItem) => this.authenticationInfoService.authentificatedUser.GroupsMembership.SupervisionGroups.includes(g.Id));
    }
    else {
      this.selectedItemsGroups = groups;
    }
    this.onGroupsChange();
  }

  onGroupsChange(): void {
    this.invoiceService.getRejectedInvoices(this.selectedItemsGroups.map((group: ReferentialItem) => group.Id))
      .subscribe(invoices => {
        this.invoices = invoices;
        this.filterInvoices();
      }, () => {
        this.data = [];
      });
  }

  onSelectAll(items: any, filter: string): void {
    switch (filter) {
      case 'RejectionFlag':
        this.selectedItemsRejectionFlag = items;
        this.filterInvoices();
        break;
      case 'ReverseFactoring':
        this.selectedItemsReverseFactoring = items;
        this.filterInvoices();
        break;
      case 'Picked':
        this.selectedItemsPicked = items;
        this.filterInvoices();
        break;
      case 'Groups':
        this.selectedItemsGroups = items;
        this.onGroupsChange();
        break;
      case 'WorkingSituation':
        this.selectedItemsWorkingSituation = items;
        this.filterInvoices();
        break;
      case 'Assets':
        this.selectedAssetsItems = items;
        this.filterInvoices();
        break;
      case 'RecipientEntity':
        this.selectedRecipientEntitiesItems = items;
        this.filterInvoices();
        break;
    }
  }

  onZoom = (data: InvoiceModel) => {
    this.router.navigate(['invoices', InvoiceViewType.REJECTED, 'detail', data.Id]);
  }

  onOpenPdf = (data: InvoiceModel) => {
    window.open(`invoices/${InvoiceViewType.REJECTED}/detail-pdf/${data.Id}`, '_blank');
  }

  onClearFilters = (): void => {
    this.selectedItemsReverseFactoring = [];
    this.selectedItemsWorkingSituation = [];
    this.selectedItemsPicked = [];
    this.selectedItemsRejectionFlag = [];
    this.selectedAssetsItems = [];
    this.selectedRecipientEntitiesItems = [];
    this.selectedItemsGroups = [];
    this.invoiceSearchCriteriaModel = new InvoiceSearchCriteriaModel();
    this.onGroupsChange();
    this.onFiltersCleared.next();
  }

  dateChangeSearch(result: any) {
    this.dateChange(result.type, result.selectedDate);
    this.filterInvoices();
  }

  filterInvoices(): void {
    this.data = this.innerFilterInvoices(this.invoices, {
      Assets: this.selectedAssetsItems,
      RecipientEntities: this.selectedRecipientEntitiesItems,
      ReverseFactoring: this.selectedItemsReverseFactoring,
      WorkingSituation: this.selectedItemsWorkingSituation,
      Picked: this.selectedItemsPicked,
      RejectionTypes: this.selectedItemsRejectionFlag,
      Groups: this.selectedItemsGroups
    } as InvoiceFilters, true);
  }

  protected onAssetFetched(assets: ReferentialItem[]) {
    this.assetsDropdownList = assets;
  }

  protected onRecipientEntitiesFetched(recipientEntities: ReferentialItem[]) {
    this.recipientEntitiesDropdownList = recipientEntities;
  }

  ngOnDestroy() {
    this.unsubscribe$.next(true);
  }

  exportAsExcel() : void {
    this.onExportExcelCaller.next();
  }

  onShowFilter(panelvalue:boolean) {
    this.panelOpenState = panelvalue;
  }
}
